import { apiGetRequest, apiPostRequest } from "../utils/webrequests";
import { Room } from "../data/rooms";
import { TimeSlot } from "../data/timeSlots";
import {PartnerInfo, PartnerListItem} from "../data/partnerData";

export async function getTimeSlots(eventId: string): Promise<TimeSlot[]> {
    try {
        console.log(`Fetching time slots for event: ${eventId}`);

        // Make API call with improved debugging
        const response = await apiGetRequest(`GetTimeSlots/${eventId}`, null);

        // Log the raw response for debugging
        console.log('Time slots raw response:', response);

        // Check if response is valid
        if (!response) {
            console.warn('Time slots response is null or undefined');
            return [];
        }

        // Try to handle different response formats
        let timeSlots: TimeSlot[];

        if (typeof response === 'string') {
            // If response is a string, try to parse it
            try {
                timeSlots = JSON.parse(response) as TimeSlot[];
                console.log('Parsed time slots from string:', timeSlots);
            } catch (parseError) {
                console.error('Error parsing time slots string:', parseError);
                return [];
            }
        } else if (Array.isArray(response)) {
            // If response is already an array, use it directly
            timeSlots = response as TimeSlot[];
            console.log('Time slots array from response:', timeSlots);
        } else {
            // If response is some other object, log it and return empty
            console.error('Unexpected time slots response format:', response);
            return [];
        }

        return timeSlots;
    } catch (error: any) {
        console.error('Error in getTimeSlots:', error);
        throw new Error('Error fetching time slots list: ' + error.message);
    }
}

export async function getRooms(eventId: string): Promise<Room[]> {
    try {
        console.log(`Fetching rooms for event: ${eventId}`);

        // Make API call with improved debugging
        const response = await apiGetRequest(`rooms/${eventId}`, null);

        // Log the raw response for debugging
        console.log('Rooms raw response:', response);

        // Check if response is valid
        if (!response) {
            console.warn('Rooms response is null or undefined');
            return [];
        }

        // Try to handle different response formats
        let rooms: Room[];

        if (typeof response === 'string') {
            // If response is a string, try to parse it
            try {
                rooms = JSON.parse(response) as Room[];
                console.log('Parsed rooms from string:', rooms);
            } catch (parseError) {
                console.error('Error parsing rooms string:', parseError);
                return [];
            }
        } else if (Array.isArray(response)) {
            // If response is already an array, use it directly
            rooms = response as Room[];
            console.log('Rooms array from response:', rooms);
        } else {
            // If response is some other object, log it and return empty
            console.error('Unexpected rooms response format:', response);
            return [];
        }

        return rooms;
    } catch (error: any) {
        console.error('Error in getRooms:', error);
        throw new Error('Error fetching room list: ' + error.message);
    }
}

export async function getScheduledSessions(eventId: string): Promise<any[]> {
    try {
        const response = await apiGetRequest(`schedule/${eventId}`, null);
        if (!response) return [];
        return typeof response === 'string' ? JSON.parse(response) : response;
    } catch (error: any) {
        console.error('Error fetching scheduled sessions:', error);
        throw new Error('Error fetching scheduled sessions: ' + error.message);
    }
}

export async function updateSchedule(scheduleData: any[]): Promise<void> {
    try {
        await apiPostRequest("UpdateSchedule", scheduleData);
    } catch (error: any) {
        console.error('Error updating schedule:', error);
        throw new Error('Error updating schedule: ' + error.message);
    }
}

export async function deleteSchedule(eventId: string): Promise<void> {
    try {
        await apiPostRequest("DeleteSchedule", { eventId });
    } catch (error: any) {
        console.error('Error deleting schedule:', error);
        throw new Error('Error deleting schedule: ' + error.message);
    }
}
export async function deleteRoom(room: Room): Promise<boolean> {
    try {
        const response = await apiPostRequest(`events/${room.EventId}/${room.RoomId}`, {});
        return true;
    } catch (error: any) {
        if (error.status === 404) {
            throw new Error('Room not found');
        } else if (error.status === 401) {
            throw new Error('Unauthorized to delete room');
        }
        throw new Error('Error deleting room: ' + error.message);
    }
}

export async function addRoom(room: Room): Promise<Room> {
    try {
        // Make an API GET request to fetch event info
        console.log("Adding room:", room);

        const data: Room = await apiPostRequest("AddRoom", room)
        // Return the fetched data
        return data;

    } catch (error: any) {
        console.error('Error in addRoom:', error);
        throw new Error('Error adding room: ' + error.message);
    }
}

export async function editRoom(room: Room): Promise<boolean> {
    try {
        const response = await apiPostRequest(`EditRoom`, room);

        // You can adjust this check based on what your backend returns
        return response?.success === true || response?.status === 'ok' || true;
    } catch (error: any) {
        if (error.status === 404) {
            throw new Error('Room not found');
        } else if (error.status === 401) {
            throw new Error('Unauthorized to edit room');
        }
        throw new Error('Error editing room: ' + error.message);
    }
}

export async function updateTimeSlot(slot: TimeSlot): Promise<TimeSlot> {
    try {
        console.log("Updating time slot:", slot);
        const data: TimeSlot = await apiPostRequest("UpdateTimeSlot", slot);
        return data;
    } catch (error: any) {
        console.error("Error in updateTimeSlot:", error);
        throw new Error("Error updating time slot: " + error.message);
    }
}

export async function deleteTimeSlot(deleteReq:any): Promise<boolean> {
    try {
        console.log(`Deleting time slot`);
        await apiPostRequest(`DeleteTimeSlot`, deleteReq);
        return true;
    } catch (error: any) {
        console.error("Error in deleteTimeSlot:", error);
        if (error.status === 404) {
            throw new Error("Time slot not found");
        } else if (error.status === 401) {
            throw new Error("Unauthorized to delete time slot");
        }
        throw new Error("Error deleting time slot: " + error.message);
    }
}

/**
 * Fetch a single time slot by eventId and timeSlotId.
 */
// export async function getTimeSlots(eventId: string, timeSlotId: string): Promise<TimeSlot> {
//     try {
//         console.log(`Fetching time slot ${timeSlotId} for event ${eventId}`);
//         const response = await apiGetRequest(`GetTimeSlot/${eventId}/`, null);
//         if (typeof response === 'string') {
//             return JSON.parse(response) as TimeSlot;
//         }
//         return response as TimeSlot;
//     } catch (error: any) {
//         console.error("Error in getTimeSlot:", error);
//         throw new Error("Error fetching time slot: " + error.message);
//     }
// }
